import React, { FC, useCallback, useState, useRef, Fragment } from 'react';
import Svg from 'svg-inline-react';
import classnames from 'classnames';
import { debounce } from 'lodash';
import GlobeSvg from '@shared/assets/svgs/globe.svg';
import { TextField } from '@src/components/TextField';
import { trackEvent } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { GetPlacesQuery, PlaceTypeEnum, useGetPlacesLazyQuery } from '@src/graphql/generated';
import { OutsideClick } from '@src/components/OutsideClick';
import { useAppSelector } from '@src/redux';
import { NoticeState } from '@src/redux/reducers/notice/notice.interface';
import { getNotice } from '@src/redux/selectors/notice';
import { PlacesSearchDropdown } from '@src/containers/Source/PlacesSearchDropdown';
import { PlacesSearchProps } from './props';
import './styles.scss';

export const PlacesSearch: FC<PlacesSearchProps> = ({
  selectedPlace,
  onPlaceSelect,
  isRelocation,
  isOnSite,
  onSiteToggle,
  onSelectGlobal,
  isRemote,
  onRelocationToggle,
  onRemoteToggle,
  onClear,
  onDropdownToggle,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const noticeData = useAppSelector<NoticeState>(getNotice);

  const [searchValue, setSearchValue] = useState(selectedPlace?.name || '');

  const [isOpen, setIsOpen] = useState(false);

  const [getPlaces, { data, loading: isLocationsLoading }] = useGetPlacesLazyQuery();

  const locations = data?.places.result || [];

  const handleToggle = (isOpen: boolean) => {
    setIsOpen(isOpen);
    onDropdownToggle(isOpen);
  };

  const setPlace = (place: GetPlacesQuery['places']['result'][0]) => {
    if (place?.entity?.id) {
      onPlaceSelect({
        id: place.entity.id,
        name: place.entity.longName,
        type: place.type,
      });

      setSearchValue(place.entity?.longName || '');
      handleToggle(false);

      if (inputRef.current) {
        inputRef.current.value = place.entity?.longName;
      }

      trackEvent({
        context: MetricContext.TALENT_AGENT_PAGE,
        interaction: MetricAction.FILTER_TALENT_AGENT_USERS,
        payload: {
          'Filter Type': 'Country or City',
          'Filter Value': place.entity?.longName,
        },
      });
    }
  };

  const onSelectGlobalOption = (): void => {
    setIsOpen(false);
    setSearchValue('');
    onSelectGlobal();
  };

  const handleGetPlaces = useCallback(
    debounce((searchValue: string) => {
      getPlaces({
        variables: {
          paginationOptions: {
            limit: 8,
          },
          placeTypes: [PlaceTypeEnum.LOCATION, PlaceTypeEnum.COUNTRY],
          searchPattern: searchValue.trim(),
        },
      });
    }, 350),
    [],
  );

  const onSearch = (searchValue: string) => {
    const doesValueExist = !!searchValue?.trim().length;

    handleGetPlaces(searchValue);
    setSearchValue(searchValue);
    handleToggle(doesValueExist);

    if (!doesValueExist) {
      onClear();
    }
  };

  const doesNoticeExist = !noticeData.isLoading && !!noticeData.notice;

  return (
    <Fragment>
      <div
        className={classnames(
          'talent-pipeline-location-search__backdrop',
          isOpen && 'talent-pipeline-location-search__backdrop--shown',
          doesNoticeExist && 'talent-pipeline-location-search__backdrop--small',
        )}
      />

      <OutsideClick addOutsideClickListener={isOpen} onOutsideClick={() => handleToggle(false)}>
        <div className="talent-pipeline-location-search">
          <Svg
            className={classnames(
              'talent-pipeline-location-search__icon',
              isOpen && 'talent-pipeline-location-search__icon--active',
            )}
            src={GlobeSvg}
          />

          <TextField
            containerClassName="talent-pipeline-location-search__container"
            defaultValue={searchValue}
            inputClassName="talent-pipeline-location-search__field"
            onChange={(searchValue: string) => onSearch(searchValue)}
            onFocus={() => handleToggle(true)}
            placeholder="Location"
            wrapperClassName="talent-pipeline-location-search__wrapper"
          />

          {isOpen && (
            <PlacesSearchDropdown
              isLoading={isLocationsLoading}
              isOnSite={isOnSite}
              isRelocation={isRelocation}
              isRemote={isRemote}
              locations={locations}
              onRelocationToggle={onRelocationToggle}
              onRemoteToggle={onRemoteToggle}
              onSelectGlobal={onSelectGlobalOption}
              onSiteToggle={onSiteToggle}
              searchValue={searchValue}
              setPlace={setPlace}
            />
          )}
        </div>
      </OutsideClick>
    </Fragment>
  );
};
